import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { Button, Card, colors, Page, Divider, ElvaLogoName, Inline, Input, Stack, PasswordInput, u } from '@ui';

import { useUserRegister } from '../../hooks';

import * as css from './UserRegisterScreen.css';

const formSchema = z
    .object({
        email: z.email(),
        name: z.string().min(3),
        password: z
            .string()
            .min(8, 'Password must be at least 8 characters')
            .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
            .regex(/[0-9]/, 'Must contain at least one number'),
        confirmPassword: z.string().min(1, 'Please confirm your password'),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: 'Passwords do not match',
        path: ['confirmPassword'],
    });

type FormSchema = z.infer<typeof formSchema>;

type UserRegisterScreenProps = {
    confirmationPath: string;
};

export const UserRegisterScreen = ({ confirmationPath }: UserRegisterScreenProps) => {
    const { register, handleSubmit, formState } = useForm<FormSchema>({
        resolver: zodResolver(formSchema),
    });

    const userRegister = useUserRegister(confirmationPath);
    const navigate = useNavigate();

    return (
        <Page>
            <Card>
                <Stack gap={20} style={{ width: '350px' }}>
                    <Inline alignItems="center" justifyContent="space-between">
                        <Stack gap={u(0.5)}>
                            <h4>Register</h4>
                            <p style={{ fontSize: '0.8em', color: colors.gray[400] }}>Create your account</p>
                        </Stack>
                        <ElvaLogoName width={90} />
                    </Inline>
                    <Divider />
                    <form onSubmit={handleSubmit((data) => userRegister.mutate(data))}>
                        <Stack gap={u(2.5)} className={css.inputsContainer} style={{ width: '100%' }}>
                            {userRegister.error && (
                                <span className={css.errorMessage}>{userRegister.error.message}</span>
                            )}
                            <Input
                                {...register('name')}
                                required
                                type="text"
                                label="Name"
                                placeholder="Your name"
                                error={formState.errors.name?.message}
                            />
                            <Input
                                {...register('email')}
                                required
                                type="email"
                                label="Email address"
                                placeholder="you@example.com"
                                error={formState.errors.email?.message}
                            />
                            <PasswordInput
                                {...register('password')}
                                required
                                label="Password"
                                placeholder="Enter your password"
                                error={formState.errors.password?.message}
                            />
                            <PasswordInput
                                label="Confirm password"
                                {...register('confirmPassword')}
                                placeholder="Confirm your password"
                                error={formState.errors.confirmPassword?.message}
                            />

                            <Inline style={{ justifyContent: 'space-between' }}>
                                <Button type="button" variant="secondary" onClick={() => navigate('/')}>
                                    Back
                                </Button>

                                <Button
                                    type="submit"
                                    isLoading={userRegister.isPending}
                                    isDisabled={userRegister.isPending}
                                >
                                    Register
                                </Button>
                            </Inline>
                        </Stack>
                    </form>
                </Stack>
            </Card>
        </Page>
    );
};
